/**
 * Help Icon Component
 * Reusable SVG help/info icon for toolbars and controls
 * Shows a document/page icon to represent documentation and help
 */

import React from 'react';

export interface HelpIconProps {
  /**
   * Icon color (default: #666)
   */
  color?: string;

  /**
   * Icon size in pixels (default: 16)
   */
  size?: number;

  /**
   * CSS class for styling
   */
  className?: string;

  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;

  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Help Icon - Document/Page icon
 * Displays a document with lines representing documentation and help reference material
 */
export const HelpIcon: React.FC<HelpIconProps> = ({
  color = '#666',
  size = 16,
  className = '',
  ariaLabel = 'Help',
  onClick,
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`af-help-icon ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'color 200ms ease',
        flexShrink: 0,
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          onClick(e as any);
        }
      }}
    >
      {/* Document page - outer rectangle */}
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />

      {/* Top right corner fold */}
      <polyline points="14 2 14 8 20 8" />

      {/* Text lines on document */}
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="15" y2="16" />
      <line x1="9" y1="20" x2="13" y2="20" />
    </svg>
  );
};

export default HelpIcon;
